import React, {type ReactNode, type JSX} from 'react';
import {ExpandMore} from '@mui/icons-material';
import {
  Accordion as MuiAccordion,
  AccordionDetails as MuiAccordionDetails,
  AccordionSummary as MuiAccordionSummary,
} from '@mui/material';
import {styled} from '@mui/material/styles';

type Props = {
  content: Array<{id: number; title: ReactNode; body: ReactNode}>;
};

const StyledAccordion = styled(MuiAccordion)(({theme}) => ({
  '&.MuiAccordion-root': {
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: 'transparent',
    boxShadow: 'none',
    borderRadius: 0,
    '&:first-of-type': {
      borderRadius: 0,
    },
    '&:last-of-type': {
      borderRadius: 0,
    },
    '&.Mui-expanded': {
      margin: 0,
    },
    '&:before': {
      display: 'none',
    },
    '&:last-child': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
}));

const StyledAccordionSummary = styled(MuiAccordionSummary)({
  '&.MuiAccordionSummary-root': {
    backgroundColor: 'transparent',
    minHeight: '60px',
    height: '60px',
    paddingLeft: 0,
    '&.Mui-expanded': {
      height: '60px',
      minHeight: '60px',
    },
  },
});

const StyledAccordionDetails = styled(MuiAccordionDetails)(({theme}) => ({
  '&.MuiAccordionDetails-root': {
    display: 'block',
    paddingLeft: 0,
    paddingRight: theme.spacing(6),
  },
}));

function Accordion({content}: Readonly<Props>): JSX.Element {
  return (
    <>
      {content.map(({id, title, body}) => (
        <StyledAccordion key={id}>
          <StyledAccordionSummary
            aria-controls={`panel1d-content-${id}`}
            id={`panel1d-header-${id}`}
            expandIcon={<ExpandMore />}
          >
            {title}
          </StyledAccordionSummary>
          <StyledAccordionDetails>{body}</StyledAccordionDetails>
        </StyledAccordion>
      ))}
    </>
  );
}

export default Accordion;
